import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants/routes';
import type {
  Deal,
  DealFilters,
  CreateDealData,
  UpdateDealData,
  MoveDealData,
  ReorderItem,
} from '@/types/deal';

const KEYS = {
  all: ['deals'] as const,
  list: (filters?: DealFilters) => [...KEYS.all, 'list', filters] as const,
  detail: (id: string) => [...KEYS.all, 'detail', id] as const,
  calendar: (start: string, end: string) => [...KEYS.all, 'calendar', start, end] as const,
  clients: (q: string) => [...KEYS.all, 'clients', q] as const,
};

export const useDeals = (filters?: DealFilters) =>
  useQuery<Deal[]>({
    queryKey: KEYS.list(filters),
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters?.status) params.set('status', filters.status);
      if (filters?.priority) params.set('priority', filters.priority);
      if (filters?.assigned_to) params.set('assigned_to', String(filters.assigned_to));
      if (filters?.search) params.set('search', filters.search);
      const qs = params.toString();
      return api.get(`${API_ROUTES.deals}${qs ? `?${qs}` : ''}`).then((r) => {
        const data = r.data;
        return Array.isArray(data) ? data : (data.results ?? []);
      });
    },
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

export const useDeal = (id: string | null) =>
  useQuery<Deal>({
    queryKey: KEYS.detail(id!),
    queryFn: () => api.get(`${API_ROUTES.deals}${id}/`).then((r) => r.data),
    enabled: id !== null,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

export const useDealsCalendar = (start: string, end: string) =>
  useQuery<Deal[]>({
    queryKey: KEYS.calendar(start, end),
    queryFn: () =>
      api.get(`${API_ROUTES.deals}calendar/?start=${start}&end=${end}`).then((r) => {
        const data = r.data;
        return Array.isArray(data) ? data : (data.results ?? []);
      }),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

export const useDealClients = (q: string) =>
  useQuery<string[]>({
    queryKey: KEYS.clients(q),
    queryFn: () =>
      api.get(`${API_ROUTES.deals}clients/?q=${encodeURIComponent(q)}`).then((r) => r.data),
    enabled: q.length >= 1,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

export const useCreateDeal = () => {
  const queryClient = useQueryClient();
  return useMutation<Deal, Error, CreateDealData>({
    mutationFn: (data) => api.post(API_ROUTES.deals, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
    },
  });
};

export const useUpdateDeal = () => {
  const queryClient = useQueryClient();
  return useMutation<Deal, Error, { id: string; data: UpdateDealData }>({
    mutationFn: ({ id, data }) => api.patch(`${API_ROUTES.deals}${id}/`, data).then((r) => r.data),
    onSuccess: (deal) => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
      queryClient.setQueryData(KEYS.detail(deal.id), deal);
    },
  });
};

export const useDeleteDeal = () => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => api.delete(`${API_ROUTES.deals}${id}/`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
    },
  });
};

export const useMoveDeal = () => {
  const queryClient = useQueryClient();
  return useMutation<Deal, Error, { id: string; data: MoveDealData }>({
    mutationFn: ({ id, data }) =>
      api.patch(`${API_ROUTES.deals}${id}/move/`, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
    },
  });
};

export const useReorderDeals = () => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, ReorderItem[]>({
    mutationFn: (items) => api.patch(`${API_ROUTES.deals}reorder/`, { items }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
    },
  });
};
